import Api from "@/api";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as Companies from "@/models/companies";
import * as React from "react";

import { PrimaryButton, showErrorToast, showSuccessToast } from "turboui";

import { usePaths } from "@/routes/paths";
import { PageModule } from "@/routes/types";

export default { name: "CompanyGuardrailsPage", loader, Page } as PageModule;

interface LoaderResult {
  company: Companies.Company;
}

async function loader(): Promise<LoaderResult> {
  return {
    company: await Companies.getCompany({}).then((d) => d.company!),
  };
}

// Keys mirror Operately.Companies.Guardrails.defaults/0 (snake_case on purpose: the
// stored map and the Highway gateway both use these exact names).
// `cents: true` fields are STORED in cents (the gateway enforces ad/promo caps in cents) but
// ENTERED and shown in whole dollars -- parseGuardrails divides by 100 on load, handleSave
// multiplies by 100 on save. `defaultValue` stays in the stored unit (cents) to mirror the backend.
const GUARDRAILS: { key: string; label: string; help: string; defaultValue: number; cents?: boolean }[] = [
  {
    key: "promo_max_percent_off",
    label: "Max promo discount (%)",
    help: "The largest percent-off discount an agent can issue without your approval.",
    defaultValue: 20,
  },
  {
    key: "promo_max_amount_off_cents",
    label: "Max promo amount off ($)",
    help: "The largest fixed-amount discount, in dollars, an agent can issue without your approval.",
    defaultValue: 5000,
    cents: true,
  },
  {
    key: "promo_max_duration_months",
    label: "Max promo duration (months)",
    help: "The longest a repeating promo created by an agent can last.",
    defaultValue: 2,
  },
  {
    key: "promo_max_per_month",
    label: "Max promos per month",
    help: "How many promo codes the whole AI workforce can create per calendar month.",
    defaultValue: 10,
  },
  {
    key: "pause_max_days",
    label: "Max subscription pause (days)",
    help: "The longest an agent can pause a customer's subscription.",
    defaultValue: 60,
  },
  {
    key: "ad_monthly_cap_cents",
    label: "Monthly ad spend cap ($)",
    help: "Total ad spend allowed per calendar month, in dollars. 0 keeps ads OFF until you raise it.",
    defaultValue: 0,
    cents: true,
  },
  {
    key: "ad_daily_cap_cents",
    label: "Daily ad spend cap ($)",
    help: "Total ad spend allowed per day, in dollars. 0 keeps ads OFF until you raise it.",
    defaultValue: 0,
    cents: true,
  },
  {
    key: "interview_max_per_week",
    label: "Max interview emails per week",
    help: "How many user-interview outreach emails agents may send per week.",
    defaultValue: 10,
  },
  {
    key: "activation_wake_hours",
    label: "Activation wake (hours)",
    help: "How many hours into a trial before Customer Success is woken about a user who hasn't activated.",
    defaultValue: 6,
  },
  {
    key: "articles_per_day",
    label: "Articles published per day",
    help: "How many blog articles Marketing publishes per day, spread evenly across your work hours. Marketing's recurring publish task re-tunes itself to hit this number. Set 0 to pause publishing entirely.",
    defaultValue: 1,
  },
  {
    key: "social_check_hours",
    label: "Social engagement check (hours)",
    help: "How often Social Media checks the accounts for new comments, mentions, and DMs and responds, in hours, within your work hours. Set 0 to pause.",
    defaultValue: 2,
  },
  {
    key: "feed_posts_per_day",
    label: "Feed posts per team per day",
    help: "How many posts EACH team may put on the company Feed (the front page) per day. Scarcity keeps it scannable: a denied post means 'save it for something bigger'. Set 0 to pause agent posting; your own posts are never limited.",
    defaultValue: 1,
  },
  {
    key: "group_post_cooldown_days",
    label: "Group post cooldown (days)",
    help: "After the company posts into an external group or community, how many days before it may post there again. The gateway refuses earlier posts into the SAME group (one voice per community). Replies and comments are never limited. Set 0 to pause all proactive group posting.",
    defaultValue: 14,
  },
  {
    key: "community_posts_per_day",
    label: "Community posts per day",
    help: "How many NEW posts per day into OTHER people's spaces (Facebook groups, subreddits, forums). Each group is still protected by the per-group cooldown above, so this is the across-groups total. Responding to comments is never limited. Set 0 to pause community posting.",
    defaultValue: 3,
  },
  {
    key: "own_posts_per_day",
    label: "Own-property posts per day",
    help: "How many NEW posts per day on the company's OWN properties (its Facebook Page, Pinterest boards, X handle, etc.). A separate budget from community posts -- posting on your own megaphone never burns a community slot. Set 0 to pause.",
    defaultValue: 2,
  },
  {
    key: "own_post_cooldown_hours",
    label: "Own-property cooldown (hours)",
    help: "How long after posting on one of your OWN properties before posting on that same property again. 20 hours allows a healthy daily cadence with room for schedule drift. Communities use the day-based cooldown above instead.",
    defaultValue: 20,
  },
  {
    key: "partner_outreach_per_day",
    label: "New partner contacts per day",
    help: "How many NEW partnership prospects Partnerships finds and contacts per day via its recurring prospecting task. Daily on purpose: one new at-bat every day beats a Monday batch followed by six days of 'quota done'. Follow-ups on existing conversations are never limited. Set 0 to pause prospecting.",
    defaultValue: 1,
  },
];

// The backend serializes guardrails as a JSON string (snake_case keys). Merge whatever
// it returned over the defaults so the form always shows a full, ordered set.
function parseGuardrails(raw?: string | null): Record<string, number> {
  let parsed: Record<string, unknown> = {};

  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = {};
    }
  }

  return GUARDRAILS.reduce<Record<string, number>>((acc, g) => {
    const value = parsed[g.key];
    const stored = typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : g.defaultValue;
    // cents fields are shown in dollars; everything else is stored and shown in the same unit.
    acc[g.key] = g.cents ? Math.round(stored / 100) : stored;
    return acc;
  }, {});
}

function Page() {
  const paths = usePaths();
  const { company } = Pages.useLoadedData<LoaderResult>();

  return (
    <Pages.Page title={["Guardrails", company.name!]} testId="company-guardrails-page">
      <Paper.Root size="small">
        <Paper.NavigateBack to={paths.companyAdminPath()} title="Back to Company Admin" />

        <Paper.Body>
          <div className="mb-2 text-content-accent text-2xl font-bold">Guardrails</div>
          <p className="mb-6 text-sm text-content-dimmed">
            Standing limits and cadences for your AI workforce. Agents act freely inside the spending and discount
            ceilings; anything over a ceiling is denied at the gateway and comes back to you as an approval request. The
            cadence settings (like articles per day) tune how often the recurring work runs. All values are non-negative
            whole numbers.
          </p>

          <Form company={company} />
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function Form({ company }: { company: Companies.Company }) {
  const [values, setValues] = React.useState<Record<string, number>>(parseGuardrails(company.guardrails));
  const [saving, setSaving] = React.useState(false);

  const updateValue = (key: string, value: number) => {
    setValues((prev) => ({ ...prev, [key]: Math.max(0, Math.floor(value)) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert dollar-entered fields back to the stored unit (cents) before saving.
      const toStore = GUARDRAILS.reduce<Record<string, number>>((acc, g) => {
        const v = values[g.key] ?? 0;
        acc[g.key] = g.cents ? Math.round(v * 100) : v;
        return acc;
      }, {});
      await Api.editCompanyGuardrails({ companyId: company.id, guardrails: JSON.stringify(toStore) });
      showSuccessToast("Saved", "Guardrails updated");
    } catch {
      showErrorToast("Error", "Failed to save guardrails");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {GUARDRAILS.map((g) => (
        <div key={g.key} data-test-id={`guardrail-${g.key}`}>
          <label className="block text-sm font-medium text-content-base mb-1">{g.label}</label>
          <input
            type="number"
            min={0}
            step={1}
            value={values[g.key]}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) updateValue(g.key, n);
            }}
            className="w-full rounded-md border border-surface-outline bg-surface-base px-3 py-2"
            data-test-id={`guardrail-input-${g.key}`}
          />
          <p className="mt-1 text-xs text-content-dimmed">{g.help}</p>
        </div>
      ))}

      <div className="flex justify-end">
        <PrimaryButton onClick={handleSave} loading={saving} testId="save-guardrails">
          Save changes
        </PrimaryButton>
      </div>
    </div>
  );
}
